import { mediaModal } from '@/styled-system/recipes';
import { css } from '@/styled-system/css';

export const {
  photoPanel,
  mainImage,
  photoCountBadge,
  thumbnailRail,
  thumbnailButton,
  thumbnailButtonSelected,
  thumbnailImage,
  mediaPanel,
  iframeStage,
  iframe,
  videoPosterButton,
  videoPosterImage,
  videoPosterFallback,
  videoPosterScrim,
  videoPosterOverlay,
  videoPlayIcon,
  mediaItemGrid,
  mediaItemButton,
  tourCardGrid,
  tourCardGridCompact,
  tourCardMedia,
  tourCardMediaCompact,
  tourCardImage,
  tourCardFallback,
  tourCardFallbackCompact,
  tourCardScrim,
  tourCardOverlay,
  tourCardIcon,
  tourCardIconCompact,
  emptyState,
  streetViewCanvas,
  streetViewStage,
  streetViewCanvasHidden,
  streetViewStatus,
  srOnly,
} = mediaModal();

export const content = css.raw({
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
  background: 'bg.neutral',
  lg: {
    blockSize: '100%',
  },
  // C11n 10.18.1 does not expose styling for the Composite wrapper around Tabs.Root.
  '& [data-c11n-component="Modal.Body"] > div': {
    display: 'flex',
    flex: '1 1 auto',
    flexDirection: 'column',
    height: '100%',
    minHeight: 0,
  },
  // Modal.Footer's component recipe owns its inset and padding, so the floating mobile dock needs a scoped override.
  '& [data-c11n-component="Modal.Footer"]': {
    top: 'auto',
    paddingBlock: 'default',
    paddingInlineStart: 'default',
    paddingInlineEnd: 'calc({spacing.default} + var(--floating-ui-scrollbar-width))',
  },
});

export const hiddenModalHeader = css.raw({
  position: 'absolute',
  paddingBlock: 'none',
  paddingInline: 'none',
});

export const mediaHeaderActions = css({
  display: 'flex',
  position: 'absolute',
  top: 'default',
  right: 'default',
  marginBlockStart: 'tightest',
  zIndex: 'zIndexOverParent',
});

export const mediaCloseButton = css.raw({
  position: 'fixed',
  top: 'default',
  right: 'auto',
  left: 'default',
  marginBlockStart: 'tightest',
  lg: {
    position: 'absolute',
    right: 'auto',
  },
});

export const mediaCloseControl = css.raw({
  position: 'relative',
  insetBlockStart: 'auto',
  insetInlineEnd: 'auto',
  zIndex: 'zIndexStep',
});

export const mediaBody = css.raw({
  display: 'flex',
  flex: '1 1 auto',
  flexDirection: 'column',
  minHeight: 0,
  overflowY: 'hidden',
  paddingBlock: 'none',
  paddingInline: 'none',
});

export const mediaDesktopTabsBar = css({
  display: 'flex',
  position: 'absolute',
  top: 'default',
  left: '50%',
  marginBlockStart: 'tightest',
  width: 'auto',
  maxWidth: 'calc(100% - {spacing.layout.vast.loose} * 4)',
  transform: 'translateX(-50%)',
  zIndex: 'zIndexOverParent',
  pointerEvents: 'auto',
});

export const mediaMobileTabsBar = css({
  width: '100%',
  pointerEvents: 'auto',
  '& > [data-c11n-component="ToggleButtonGroup"]': {
    width: '100%',
  },
});

export const mediaCategoryToggleButton = css.raw({
  minWidth: 0,
  paddingInline: 'default',
  whiteSpace: 'nowrap',
});

export const mediaPanelViewport = css.raw({
  position: 'relative',
  display: 'flex',
  flex: '1 1 auto',
  minHeight: 0,
  overflow: 'hidden',
});

export const mediaFooter = css.raw({
  position: 'absolute',
  top: 'auto',
  right: 0,
  bottom: 0,
  left: 0,
  width: '100%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 'tighter',
  paddingBlock: 'default',
  paddingInlineStart: 'default',
  paddingInlineEnd: 'calc({spacing.default} + var(--floating-ui-scrollbar-width))',
  borderTopWidth: 'default',
  borderTopStyle: 'solid',
  borderTopColor: 'border.neutral',
  background: 'transparent',
  minHeight: '1800',
  zIndex: 'zIndexOverParent',
  lg: {
    display: 'none',
  },
});

export const mediaFooterGlass = css({
  boxShadow: 'sm',
  inset: 0,
  pointerEvents: 'none',
  position: 'absolute',
  zIndex: 'zIndexBase',
});

export const mediaDesktopFooter = css({
  position: 'absolute',
  bottom: 'default',
  left: '50%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  borderRadius: 'md',
  padding: 'tighter',
  transform: 'translateX(-50%)',
  zIndex: 'zIndexOverParent',
  pointerEvents: 'auto',
});

export const mediaDesktopFooterGlass = css({
  position: 'absolute',
  inset: 0,
  borderRadius: 'md',
  boxShadow: 'smHeavy',
  pointerEvents: 'none',
  zIndex: 'zIndexBase',
  _before: {
    borderRadius: 'md',
  },
});

export const mediaActionGroup = css.raw({
  display: 'flex',
  alignItems: 'center',
  flexWrap: 'nowrap',
  gap: 'tighter',
  justifyContent: 'flex-start',
  position: 'relative',
  width: '100%',
  pointerEvents: 'auto',
  zIndex: 'zIndexStep',
  '& > [data-media-primary-action]': {
    flexGrow: 1,
    minWidth: 0,
    whiteSpace: 'nowrap',
  },
  lg: {
    width: 'auto',
  },
});

export const mediaVerticalActionGroup = css.raw({
  position: 'relative',
  width: '100%',
  pointerEvents: 'auto',
  zIndex: 'zIndexStep',
});

export const mediaActionSecondaryOnImpact = css({
  _hover: {
    boxShadowColor: 'border.action.neutral-fixed.hover',
  },
  _active: {
    boxShadowColor: 'border.action.neutral-fixed.pressed',
  },
});

export const mediaMobileHeaderActions = css({
  position: 'fixed',
  top: 'default',
  right: 'default',
  zIndex: 'zIndexOverParent',
  pointerEvents: 'auto',
  lg: {
    display: 'none',
  },
});

export const mediaPanelWithMobileDockClearance = css({
  paddingBlockEnd: 'calc({spacing.layout.loosest} * 2 + {spacing.default})',
  lg: {
    paddingBlockEnd: 'default',
  },
});

export const photoGalleryScroller = css.raw({
  position: 'absolute',
  // Panda does not emit a valid inset utility for semantic `none`; generated `zero` is required here.
  inset: 'zero',
  overflowY: 'auto',
  overscrollBehavior: 'contain',
  paddingBlockStart: 'zero',
  paddingBlockEnd: 'mediaModal.mobileDockClearance',
  paddingInline: 'zero',
  scrollPaddingBlockEnd: 'mediaModal.mobileDockClearance',
  scrollbarWidth: 'none',
  '&::-webkit-scrollbar': {
    display: 'none',
  },
  lg: {
    paddingBlock: 'zero',
    paddingInline: 'zero',
    scrollBehavior: 'smooth',
    scrollPaddingBlockEnd: 'zero',
  },
  '@media (prefers-reduced-motion: reduce)': {
    scrollBehavior: 'auto',
  },
});

export const mediaEmptyState = css.raw({
  display: 'flex',
  minHeight: 'obj.xxl',
  alignItems: 'center',
  justifyContent: 'center',
  borderRadius: 'md',
  background: 'bg.softest',
  padding: 'loose',
  textAlign: 'center',
});

export const photoGalleryGrid = css.raw({
  display: 'grid',
  gridTemplateColumns: 'minmax(0, 1fr)',
  gap: 'tighter',
  width: '100%',
  // Product-specific media gallery width has no semantic C11n token.
  maxWidth: '[1280px]',
  marginInline: 'auto',
  lg: {
    gridTemplateColumns: 'repeat(10, minmax(0, 1fr))',
    // Product-specific media row height has no semantic C11n token.
    gridAutoRows: '[400px]',
  },
});

export const photoGalleryCellWide = css.raw({
  display: 'block',
  width: '100%',
  aspectRatio: '4 / 3',
  overflow: 'hidden',
  borderRadius: 'md',
  background: 'bg.softest',
  cursor: 'pointer',
  _focusVisible: {
    boxShadow: 'focus',
  },
  lg: {
    height: '100%',
    aspectRatio: 'auto',
    gridColumn: 'span 6',
  },
});

export const photoGalleryCellNarrow = css.raw({
  display: 'block',
  width: '100%',
  aspectRatio: '4 / 3',
  overflow: 'hidden',
  borderRadius: 'md',
  background: 'bg.softest',
  cursor: 'pointer',
  _focusVisible: {
    boxShadow: 'focus',
  },
  lg: {
    height: '100%',
    aspectRatio: 'auto',
    gridColumn: 'span 4',
  },
});

export const photoGalleryFallbackCellWide = css.raw({
  display: 'block',
  width: '100%',
  aspectRatio: '4 / 3',
  overflow: 'hidden',
  borderRadius: 'md',
  background: 'bg.softest',
  cursor: 'default',
  lg: {
    height: '100%',
    aspectRatio: 'auto',
    gridColumn: 'span 6',
  },
});

export const photoGalleryFallbackCellNarrow = css.raw({
  display: 'block',
  width: '100%',
  aspectRatio: '4 / 3',
  overflow: 'hidden',
  borderRadius: 'md',
  background: 'bg.softest',
  cursor: 'default',
  lg: {
    height: '100%',
    aspectRatio: 'auto',
    gridColumn: 'span 4',
  },
});

export const photoGalleryImage = css({
  display: 'block',
  width: '100%',
  height: '100%',
  objectFit: 'cover',
  pointerEvents: 'none',
});

export const lightboxContent = css.raw({
  display: 'flex',
  flexDirection: 'column',
  height: '100dvh',
  maxHeight: '100dvh',
  overflow: 'hidden',
  background: 'bg.impact-fixed',
});

export const lightboxBody = css.raw({
  display: 'flex',
  flex: '1 1 auto',
  minHeight: 0,
  overflow: 'hidden',
  paddingBlock: 'none',
  paddingInline: 'none',
});

export const lightboxStage = css.raw({
  position: 'relative',
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
  height: '100%',
  minHeight: 0,
  borderRadius: 'none',
  background: 'bg.impact-fixed',
  // The lightbox supports native horizontal swiping, vertical panning, and browser pinch zoom.
  touchAction: '[pan-x pan-y pinch-zoom]',
});

export const mobileLightboxCarousel = css.raw({
  display: 'flex',
  flex: '1 1 auto',
  flexDirection: 'column',
  width: '100%',
  minHeight: 0,
  overflow: 'hidden',
  borderRadius: 'none',
  background: 'bg.impact-fixed',
  '& [data-c11n-component="Carousel.ScrollContainer"]': {
    flex: '1 1 auto',
    minHeight: 0,
    alignItems: 'center',
  },
  '& [data-c11n-component="Carousel.Slide"]': {
    display: 'flex',
    alignItems: 'center',
    height: '100%',
  },
});

export const mobileLightboxPhoto = css.raw({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 'tighter',
  width: '100%',
  height: '100%',
  minHeight: 0,
});

export const mobileLightboxImage = css({
  flex: '0 1 auto',
  width: 'auto',
  height: 'auto',
  maxWidth: '100%',
  maxHeight: '100%',
  minHeight: 0,
  objectFit: 'contain',
});

export const desktopLightboxLayout = css.raw({
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
  height: '100%',
  minHeight: 0,
  background: 'bg.impact-fixed',
});

export const desktopLightboxStage = css.raw({
  display: 'flex',
  flex: '1 1 auto',
  flexDirection: 'column',
  width: '100%',
  minHeight: 0,
  overflow: 'hidden',
  borderRadius: 'none',
  background: 'bg.impact-fixed',
  scrollBehavior: 'auto',
});

export const lightboxThumbnailRail = css({
  position: 'relative',
  flex: '0 0 auto',
  width: '100%',
  background: 'bg.impact-fixed',
  paddingBlock: 'tighter',
});

export const lightboxThumbnailNavControls = css.raw({
  position: 'absolute',
  inset: 'zero',
  alignItems: 'center',
  justifyContent: 'space-between',
  paddingInline: 'tighter',
  zIndex: 'zIndexOverParent',
  pointerEvents: 'none',
});

export const lightboxThumbnailNavButton = css.raw({
  background: 'bg.neutral',
  boxShadow: 'md',
  pointerEvents: 'auto',
  _hover: {
    background: 'bg.softest',
  },
});

export const lightboxCloseButton = css.raw({
  position: 'fixed',
  top: 'default',
  right: 'auto',
  left: 'default',
  marginBlockStart: 'tightest',
  lg: {
    position: 'absolute',
    right: 'auto',
  },
});

export const lightboxThumbnailButton = css({
  flex: '0 0 auto',
  width: '2400',
  height: '1800',
  overflow: 'hidden',
  borderRadius: 'md',
  borderWidth: 'thick',
  borderStyle: 'solid',
  borderColor: 'border.neutral',
  background: 'bg.softest',
  cursor: 'pointer',
  padding: 'zero',
  _hover: {
    borderColor: 'border.accent.brand.hero',
  },
  _focusVisible: {
    boxShadow: 'focus',
  },
});

export const lightboxThumbnailButtonSelected = css({
  borderColor: 'border.accent.brand.hero',
});

export const lightboxThumbnailImage = css({
  width: '100%',
  height: '100%',
  objectFit: 'cover',
});

export const lightboxPreviousButton = css.raw({
  background: 'bg.neutral',
  boxShadow: 'md',
  position: 'absolute',
  // Panda does not emit a valid inset utility for semantic `none`; generated `zero` is required here.
  insetBlock: 'zero',
  insetInlineStart: 'default',
  zIndex: 'zIndexOverParent',
  marginBlock: 'auto',
  _hover: {
    background: 'bg.softest',
  },
});

export const lightboxNextButton = css.raw({
  background: 'bg.neutral',
  boxShadow: 'md',
  position: 'absolute',
  // Panda does not emit a valid inset utility for semantic `none`; generated `zero` is required here.
  insetBlock: 'zero',
  insetInlineEnd: 'default',
  zIndex: 'zIndexOverParent',
  marginBlock: 'auto',
  _hover: {
    background: 'bg.softest',
  },
});

export const lightboxDesktopActions = css.raw({
  position: 'absolute',
  display: 'flex',
  top: 'default',
  right: 'default',
  marginBlockStart: 'tightest',
  zIndex: 'zIndexOverParent',
  pointerEvents: 'auto',
});

export const lightboxDesktopPrimaryActions = css({
  position: 'absolute',
  top: 'calc({spacing.default} + {sizes.action.sm} + {spacing.loose})',
  right: 'default',
  display: 'flex',
  alignItems: 'stretch',
  width: 'max-content',
  borderRadius: 'md',
  padding: 'tighter',
  zIndex: 'zIndexOverParent',
  pointerEvents: 'auto',
});

export const lightboxDesktopPrimaryActionsGlass = css({
  position: 'absolute',
  inset: 0,
  borderRadius: 'md',
  boxShadow: 'smHeavy',
  pointerEvents: 'none',
  zIndex: 'zIndexBase',
  _before: {
    borderRadius: 'md',
  },
});

export const lightboxFooter = css.raw({
  position: 'absolute',
  inset: 'zero',
  display: 'flex',
  alignItems: 'flex-start',
  zIndex: 'zIndexOverParent',
  justifyContent: 'flex-start',
  background: 'transparent',
  paddingBlockStart: 'default',
  paddingInlineStart: 'default',
  pointerEvents: 'none',
  lg: {
    justifyContent: 'center',
    paddingInlineStart: 'none',
  },
});

export const lightboxPhotoCountSurface = css({
  position: 'relative',
  borderRadius: 'pill',
  paddingInline: 'tighter',
  paddingBlock: 'tightest',
});

export const lightboxPhotoCountGlass = css({
  position: 'absolute',
  inset: 0,
  borderRadius: 'pill',
  boxShadow: 'sm',
  pointerEvents: 'none',
  zIndex: 'zIndexBase',
  _before: {
    borderRadius: 'pill',
  },
});

export const lightboxPhotoCount = css.raw({
  position: 'relative',
  color: 'text.neutral',
  zIndex: 'zIndexStep',
});

export const mobileLightboxPhotoCount = css.raw({
  position: 'absolute',
  top: 'min(calc(50% + {sizes.3600}), calc(100% - {spacing.layout.loosest}))',
  insetInlineStart: '50%',
  marginBlockStart: 'tighter',
  borderRadius: 'pill',
  background: 'bg.impact-soft-fixed',
  color: 'text.onImpact.neutral-fixed',
  paddingInline: 'tighter',
  paddingBlock: 'tightest',
  transform: 'translateX(-50%)',
  zIndex: 'zIndexOverParent',
  pointerEvents: 'none',
});

export const mainImageStage = css.raw({
  minHeight: 0,
  borderRadius: 'md',
  background: 'black',
});

export const viewportZoomGestureLayer = css.raw({
  position: 'absolute',
  // Panda does not emit a valid inset utility for semantic `none`; generated `zero` is required here.
  inset: 'zero',
  zIndex: 'zIndexBase',
});

export const tourCardButton = css({
  display: 'block',
  minHeight: '5000',
  overflow: 'hidden',
  borderRadius: 'md',
  borderWidth: 'thick',
  borderStyle: 'solid',
  borderColor: 'border.neutral',
  background: 'bg.softest',
  color: 'white',
  cursor: 'pointer',
  padding: 'zero',
  textAlign: 'center',
  _hover: {
    borderColor: 'border.accent.brand.hero',
  },
  _focusVisible: {
    outlineWidth: 'thick',
    outlineStyle: 'solid',
    outlineColor: 'border.accent.brand.hero',
    outlineOffset: '50',
  },
});

export const tourCardButtonCompact = css({
  flex: '0 0 auto',
  width: '5000',
  minHeight: '2600',
});

export const tourCardButtonSelected = css({
  borderColor: 'border.accent.brand.hero',
});

export const tourCardLabel = css({
  maxWidth: '100%',
  overflowWrap: 'anywhere',
  color: 'white',
});

export const mediaPanelWithMobileControlsClearance = css({
  paddingBlockEnd: 'calc(env(safe-area-inset-bottom) + {sizes.action.md} + ({spacing.default} * 2))',
  lg: { paddingBlockEnd: 'default' },
});

export const photoGalleryWithoutFooter = css.raw({
  paddingBlockEnd: 'calc(env(safe-area-inset-bottom) + {sizes.action.md} + ({spacing.default} * 2))',
  scrollPaddingBlockEnd: 'calc(env(safe-area-inset-bottom) + {sizes.action.md} + ({spacing.default} * 2))',
  lg: { paddingBlockEnd: 'zero', scrollPaddingBlockEnd: 'zero' },
});
